import { Card, FormControl, Input, InputLabel } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";
import React, { useEffect } from "react";
import InputMaskWrapper from "@/components/atoms/InputMaskWrapper";

import FormField from "@/components/atoms/FormField";
import { BrazilPhoneMaskInput } from "@/components/atoms/MaskInput";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { CustomerFormData } from "@/components/screens/CustomerFormPage/types";
import { applyDocumentMask, determineDocumentType } from "@/helpers/document-helpers";
import { handlePhoneChanges } from "@/helpers/phone-helpers";
import { CustomerFormMessages } from "@/messages/customer/customer-form.messages";
import { DocumentType, PersonType } from "@/types/document.types";

type Props = {
  messages: CustomerFormMessages;
};

function GeneralInfo({ messages }: Props) {
  const formik = useFormikContext<CustomerFormData>();
  const { values, errors, handleChange, setFieldValue, initialValues } = formik;
  const { federalTaxId, personType } = values;

  useEffect(() => {
    const documentType = determineDocumentType(federalTaxId);

    let newPersonType: PersonType | undefined;

    if (documentType === DocumentType.CPF) {
      newPersonType = PersonType.Individual;
    } else if (documentType === DocumentType.CNPJ) {
      newPersonType = PersonType.LegalEntity;
    }

    const shouldUpdatePersonType = newPersonType && newPersonType !== personType;

    if (shouldUpdatePersonType) {
      setFieldValue("personType", newPersonType);
    }
  }, [federalTaxId, setFieldValue, personType]);

  useEffect(() => {
    const document = federalTaxId.replace(/\D/g, "");
    if (document.length < 10) setFieldValue("federalTaxId", document);
  }, [setFieldValue, federalTaxId]);

  const isCpfOrCnpj = determineDocumentType(federalTaxId);
  const disabledFedId = isCpfOrCnpj !== "UNDEFINED" && federalTaxId === initialValues.federalTaxId;

  return (
    <Card sx={{ height: "100%" }}>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold">
            {messages.generalSection}
          </MDTypography>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid size={{ md: 4, xs: 12 }}>
              {(() => {
                return (
                  <InputMaskWrapper
                    mask={applyDocumentMask(values.federalTaxId)}
                    value={values.federalTaxId as string}
                    onChange={handleChange}
                    disabled={!!disabledFedId}
                    required
                    variant="standard"
                    name="federalTaxId"
                    label={messages.federalTaxIdLabel}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.federalTaxId}
                    helperText={errors.federalTaxId}
                  />
                );
              })()}
            </Grid>

            <Grid size={{ md: 4, xs: 12 }}>
              <FormField
                variant="standard"
                name="name"
                label={messages.nameLabel}
                value={values.name}
                onChange={handleChange}
                fullWidth
                error={!!errors.name}
              />
            </Grid>

            {values.personType === PersonType.LegalEntity && (
              <>
                <Grid size={{ md: 4, xs: 12 }}>
                  <FormField
                    variant="standard"
                    name="fantasy"
                    label={messages.fantasyLabel}
                    value={values.fantasy}
                    onChange={handleChange}
                    fullWidth
                    error={!!errors.fantasy}
                  />
                </Grid>
                <Grid size={{ md: 4, xs: 12 }}>
                  <FormField
                    variant="standard"
                    name="stateTaxId"
                    label={messages.stateTaxIdLabel}
                    value={values.stateTaxId}
                    onChange={handleChange}
                    fullWidth
                    error={!!errors.stateTaxId}
                  />
                </Grid>
                <Grid size={{ md: 4, xs: 12 }}>
                  <FormField
                    variant="standard"
                    name="cityTaxId"
                    label={messages.cityTaxIdLabel}
                    value={values.cityTaxId}
                    onChange={handleChange}
                    fullWidth
                    error={!!errors.cityTaxId}
                  />
                </Grid>

                <Grid size={{ md: 4, xs: 12 }}>
                  <FormField
                    variant="standard"
                    name="email"
                    label={messages.emailLabel}
                    value={values.email}
                    onChange={handleChange}
                    fullWidth
                    error={!!errors.email}
                  />
                </Grid>
              </>
            )}

            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl variant="standard" fullWidth>
                <InputLabel htmlFor="formatted-text-mask-input">
                  {messages.mobilePhoneLabel}
                </InputLabel>
                <Input
                  value={values.mobilePhone}
                  onChange={(e) => handlePhoneChanges("mobilePhone", e.target.value, setFieldValue)}
                  name="mobilePhone"
                  inputComponent={BrazilPhoneMaskInput}
                />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl variant="standard" fullWidth>
                <InputLabel htmlFor="formatted-text-mask-input">{messages.phone1Label}</InputLabel>
                <Input
                  value={values.phone1}
                  onChange={(e) => handlePhoneChanges("phone1", e.target.value, setFieldValue)}
                  name="phone1"
                  inputComponent={BrazilPhoneMaskInput}
                />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <FormField
                externalLabel
                variant="standard"
                name="customerDescription"
                label={messages.descriptionLabel}
                value={values.customerDescription as string}
                onChange={handleChange}
                fullWidth
                multiline
                rows={4}
                error={!!errors.customerDescription}
              />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
}

export default GeneralInfo;
